import React, { useState, useEffect } from 'react'
import PriceApi from '../../../../api/PriceApi'
import _, { isEmpty } from 'lodash'
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch
} from 'antd'
import { useDeepEffect } from '../../../../utils/hooks'

const { Option } = Select

const SOURCE_TYPES = [
  'SUNSIRS',
  'SINA_API',
  'YAHOO_FINANCE',
  'WESTMETALL_HTML',
  'LME_API',
  'LOGIN_SMM',
  'SHFE_STOCK',
  'EXCHANGE_RATE',
  'FREIGHTOS_FBX',
  'CME_GROUP',
  'CHINAISA_INVENTORY',
  'SCREEN'
]

const SOURCE_HINTS = {
  SUNSIRS: 'รหัสสินค้า เช่น 432 (Nickel)',
  SINA_API: 'Sina symbol เช่น hf_CU',
  YAHOO_FINANCE: 'Yahoo symbol เช่น HG=F',
  WESTMETALL_HTML: 'Metal slug เช่น kupfer',
  LME_API: 'LME datasource ID',
  LOGIN_SMM: 'URL หน้าราคาบน metal.com',
  SHFE_STOCK: 'SHFE symbol เช่น cu, al, zn, pb, ni, sn',
  EXCHANGE_RATE: 'คู่สกุลเงิน เช่น USD_THB',
  FREIGHTOS_FBX: 'URL หน้า FBX terminal เช่น https://www.freightos.com/enterprise/terminal/fbx-02-north-america-west-coast-to-china/',
  CME_GROUP: 'URL หน้า quotes (ราคา settle) หรือ URL ไฟล์ .xls delivery report (สต๊อก) เช่น https://www.cmegroup.com/delivery_reports/Copper_Stocks.xls',
  CHINAISA_INVENTORY: 'ใส่ INVENTORY_5VAR (สต๊อกเหล็ก 5 ชนิดของจีน หน่วย 万吨/หมื่นตัน) หรือ column ID hash ของ chinaisa.org.cn',
  SCREEN: 'TradingView URL'
}

const INDEX_TYPE_OPTIONS = [
  { value: 'PRICE', label: 'ราคา' },
  { value: 'STOCK', label: 'สต๊อก' },
  { value: 'ECONOMIC', label: 'ตัวเลขเศรษฐกิจ' },
  { value: 'OTHER', label: 'อื่นๆ' }
]

const INDEX_TYPES_WITH_CLOSE_TIME = ['PRICE', 'STOCK']

const UNIT_OPTIONS = [
  { value: 'TON', label: 'ตัน' },
  { value: 'KG', label: 'กิโลกรัม' },
  { value: 'GRAM', label: 'กรัม' },
  { value: 'POUND', label: 'ปอนด์' },
  { value: 'TROY_OZ', label: 'ออนซ์ทรอย' },
  { value: 'BARREL', label: 'บาร์เรล' },
  { value: 'LITER', label: 'ลิตร' },
  { value: 'NONE', label: 'ไม่มี' }
]

const API_BASE = '/admin'

const PriceForm = (props) => {
  const { visible, onOk, onCancel, formData, form } = props
  const { getFieldDecorator, validateFields, setFieldsValue, getFieldValue } =
    form

  const [isActive, setIsActive] = useState(true)
  const [sourceType, setSourceType] = useState('SUNSIRS')
  const [indexType, setIndexType] = useState('PRICE')
  const [sourceCurrencies, setSourceCurrencies] = useState([])
  const [targetCurrencies, setTargetCurrencies] = useState([])

  useEffect(() => {
    // Load currency catalog for source/target dropdowns.
    PriceApi
      .get(`${API_BASE}/currencies?active_only=true`)
      .then((res) => {
        const rows = res.data || []
        setSourceCurrencies(rows.filter((c) => c.is_source))
        setTargetCurrencies(rows.filter((c) => c.is_target))
      })
      .catch(() => {
        // Fall back to a minimal hardcoded list so the form still works if the endpoint is unavailable.
        setSourceCurrencies([
          { code: 'USD', name: 'US Dollar' },
          { code: 'CNY', name: 'Chinese Yuan' }
        ])
        setTargetCurrencies([{ code: 'THB', name: 'Thai Baht' }])
      })
  }, [visible])

  useDeepEffect(() => {
    if (!isEmpty(formData)) {
      setFieldsValue({
        name: formData.name,
        name_en: formData.name_en,
        index_type: formData.index_type || 'PRICE',
        source_type: formData.source_type,
        source_url: formData.source_url,
        source_currency: formData.source_currency,
        target_currency: formData.target_currency,
        source_unit: formData.source_unit || 'TON',
        target_unit: formData.target_unit || 'KG',
        vat_rate:
          typeof formData.vat_rate === 'number'
            ? formData.vat_rate * 100
            : 0,
        close_time_local: formData.close_time_local,
        timezone: formData.timezone || 'Asia/Bangkok'
      })
      setIsActive(formData.is_active !== false)
      setSourceType(formData.source_type || 'SUNSIRS')
      setIndexType(formData.index_type || 'PRICE')
    } else {
      setIsActive(true)
      setSourceType('SUNSIRS')
      setIndexType('PRICE')
    }

    return () => {
      form.resetFields()
    }
  }, [formData, visible])

  const onSubmit = (e) => {
    e.preventDefault()
    validateFields(async (err, values) => {
      if (err) return
      const data = {
        name: values.name,
        name_en: values.name_en || null,
        index_type: values.index_type || 'PRICE',
        source_type: values.source_type,
        source_url: values.source_url,
        is_active: isActive,
        timezone: values.timezone || 'Asia/Bangkok'
      }
      // PRICE-only fields — omit for other index types so backend doesn't try to convert them.
      const idxType = values.index_type || 'PRICE'
      if (idxType === 'PRICE') {
        data.source_currency = values.source_currency || null
        data.target_currency = values.target_currency || null
        data.target_unit = values.target_unit || null
        data.vat_rate =
          typeof values.vat_rate === 'number' ? values.vat_rate / 100 : 0
      }
      // STOCK and PRICE both keep source_unit (weight/volume label).
      if (idxType === 'PRICE' || idxType === 'STOCK') {
        data.source_unit = values.source_unit || null
      }
      if (INDEX_TYPES_WITH_CLOSE_TIME.includes(values.index_type || 'PRICE')) {
        data.close_time_local = values.close_time_local || null
        data.timezone = values.timezone || 'Asia/Bangkok'
      }
      if (!_.isEmpty(formData)) {
        data.id = formData.id
      }
      onOk(data)
    })
  }

  const currentSourceType = getFieldValue('source_type') || sourceType
  const sourceUrlHint = SOURCE_HINTS[currentSourceType] || 'source URL / symbol'
  const currentIndexType = getFieldValue('index_type') || indexType
  const isPriceType = currentIndexType === 'PRICE'
  const showSourceUnit = isPriceType || currentIndexType === 'STOCK'
  const hasCloseTime = INDEX_TYPES_WITH_CLOSE_TIME.includes(currentIndexType)

  return (
    <Modal
      title={`${!_.isEmpty(formData) ? 'แก้ไข' : 'เพิ่ม'} ดัชนี`}
      visible={visible}
      footer={null}
      onCancel={onCancel}
      width={720}
      destroyOnClose>
      <Form onSubmit={onSubmit} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="ชื่อดัชนี (ไทย)">
              {getFieldDecorator('name', {
                rules: [{ required: true, message: 'กรุณากรอกชื่อ' }]
              })(<Input placeholder="เช่น นิกเกิล จีน" />)}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="ชื่อดัชนี (EN)">
              {getFieldDecorator('name_en')(
                <Input placeholder="e.g. Nickel China" />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="หมวดดัชนี">
              {getFieldDecorator('index_type', {
                initialValue: 'PRICE',
                rules: [{ required: true, message: 'กรุณาเลือกหมวด' }]
              })(
                <Select
                  onChange={(value) => setIndexType(value)}
                  placeholder="เลือกหมวด">
                  {INDEX_TYPE_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Source Type">
              {getFieldDecorator('source_type', {
                initialValue: 'SUNSIRS',
                rules: [{ required: true, message: 'กรุณาเลือก source type' }]
              })(
                <Select
                  onChange={(value) => setSourceType(value)}
                  placeholder="เลือก source">
                  {SOURCE_TYPES.map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Source URL / Symbol" extra={sourceUrlHint}>
              {getFieldDecorator('source_url', {
                rules: [
                  { required: true, message: 'กรุณากรอก source URL / symbol' }
                ]
              })(<Input placeholder={sourceUrlHint} />)}
            </Form.Item>
          </Col>
        </Row>

        {isPriceType && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="สกุลเงินต้นทาง">
                {getFieldDecorator('source_currency', {
                  rules: [
                    { required: true, message: 'กรุณาเลือกสกุลเงินต้นทาง' }
                  ]
                })(
                  <Select placeholder="เลือกสกุล">
                    {sourceCurrencies.map((c) => (
                      <Option key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="สกุลเงินปลายทาง">
                {getFieldDecorator('target_currency', {
                  rules: [
                    { required: true, message: 'กรุณาเลือกสกุลเงินปลายทาง' }
                  ]
                })(
                  <Select placeholder="เลือกสกุล">
                    {targetCurrencies.map((c) => (
                      <Option key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
          </Row>
        )}

        {showSourceUnit && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="หน่วยต้นทาง">
                {getFieldDecorator('source_unit', {
                  initialValue: 'TON'
                })(
                  <Select>
                    {UNIT_OPTIONS.map((u) => (
                      <Option key={u.value} value={u.value}>
                        {u.label}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            {isPriceType && (
              <Col span={12}>
                <Form.Item label="หน่วยปลายทาง">
                  {getFieldDecorator('target_unit', {
                    initialValue: 'KG'
                  })(
                    <Select>
                      {UNIT_OPTIONS.map((u) => (
                        <Option key={u.value} value={u.value}>
                          {u.label}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            )}
          </Row>
        )}

        {isPriceType && (
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="VAT ที่หัก (%)" extra="เช่น 7 จะหัก 7% ออกจากราคา">
                {getFieldDecorator('vat_rate', { initialValue: 0 })(
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.5}
                    style={{ width: '100%' }}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
        )}

        {hasCloseTime && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="เวลาปิดตลาด"
                extra="HH:MM ตาม timezone ด้านข้าง">
                {getFieldDecorator('close_time_local')(
                  <Input placeholder="04:00" />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Timezone">
                {getFieldDecorator('timezone', {
                  initialValue: 'Asia/Bangkok'
                })(<Input placeholder="Asia/Bangkok" />)}
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Active">
              <Switch
                checked={isActive}
                onChange={(checked) => setIsActive(checked)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row type="flex" gutter={8} justify="end">
          <Col>
            <Button onClick={() => onCancel()}>ยกเลิก</Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit">
              บันทึก
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

const WrappedPriceForm = Form.create({ name: 'PriceForm' })(PriceForm)
export default WrappedPriceForm
